import * as React from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Typography, CircularProgress } from '@material-ui/core'
import { FormDataConsumer, useInput, useNotify, useRefresh } from 'react-admin'
import { useTranslate } from 'ra-core'
import envConfig from '../environments'

const resolveLogoPreviewUrl = (logoUrl, backendUrl) => {
  if (!logoUrl) {
    return null
  }
  if (/^https?:\/\//i.test(logoUrl)) {
    return logoUrl
  }
  if (logoUrl.startsWith('/')) {
    return `${backendUrl}${logoUrl}`
  }
  return `${backendUrl}/${logoUrl}`
}

const CampaignLogoUpload = ({ campaignId, logoUrl, onUploaded }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [uploading, setUploading] = React.useState(false)
  const fileInputRef = React.useRef(null)
  const previewUrl = resolveLogoPreviewUrl(logoUrl, envConfig.backendUrl)

  const handleUpload = async event => {
    const file = event.target.files && event.target.files[0]
    if (!file || !campaignId) {
      return
    }

    const formData = new FormData()
    formData.append('logo', file)

    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    const headers = new Headers()
    if (auth?.token) {
      headers.set('Authorization', `Basic ${auth.token}`)
    }

    setUploading(true)
    try {
      const response = await fetch(
        `${envConfig.backendUrl}/campaigns/${campaignId}/logo`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
      )
      const json = await response.json()
      if (!response.ok) {
        throw new Error(json.detail || json.message || 'Upload failed')
      }

      const nextLogoUrl = json.data?.theme?.logoUrl || ''
      onUploaded(nextLogoUrl)
      refresh()
      notify('surveys.campaigns.logo_upload_success', 'info')
    } catch (error) {
      notify(error.message || 'surveys.campaigns.logo_upload_error', 'warning')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  if (!campaignId) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translate('surveys.campaigns.logo_upload_after_save')}
      </Typography>
    )
  }

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" gutterBottom>
        {translate('surveys.campaigns.logo_upload_label')}
      </Typography>
      {previewUrl && (
        <Box mb={2}>
          <img
            src={previewUrl}
            alt={translate('surveys.campaigns.logo_upload_label')}
            style={{ maxWidth: 220, maxHeight: 120, objectFit: 'contain' }}
          />
        </Box>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      <Button
        variant="outlined"
        color="primary"
        disabled={uploading}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
      >
        {uploading ? (
          <CircularProgress size={20} />
        ) : (
          translate('surveys.campaigns.logo_upload_button')
        )}
      </Button>
      <Typography variant="caption" display="block" color="textSecondary">
        {translate('surveys.campaigns.logo_upload_help')}
      </Typography>
    </Box>
  )
}

CampaignLogoUpload.propTypes = {
  campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  logoUrl: PropTypes.string,
  onUploaded: PropTypes.func.isRequired,
}

const CampaignLogoInput = props => {
  const { input } = useInput(props)

  return (
    <FormDataConsumer>
      {({ formData }) => (
        <CampaignLogoUpload
          campaignId={formData.id}
          logoUrl={input.value}
          onUploaded={input.onChange}
        />
      )}
    </FormDataConsumer>
  )
}

export default CampaignLogoInput
